import React, { useEffect, useState } from 'react';
import { useGSAPReveal } from '../util/useGSAPReveal';

interface Testimonial {
    quote: string;
    name: string;
    title: string;
    relationship: string;
}

const testimonials: Testimonial[] = [
    {
        quote: "Asim is one of the most technically proficient engineers I've had the pleasure of working with. His ability to break down complex problems and architect scalable solutions is truly impressive. Beyond technical skills, his strategic mindset and leadership have been invaluable.",
        name: "Syed Faizan",
        title: "Engineering Manager",
        relationship: "Managed Asim directly",
    },
    {
        quote: "Working with Asim was a true pleasure. His deep knowledge of backend systems, microservices, and cloud infrastructure is exceptional. He consistently delivered high-quality work, and his proactive approach to solving engineering challenges set him apart from the rest of the team.",
        name: "Ahmed Raza",
        title: "Senior Software Engineer",
        relationship: "Worked with Asim",
    },
    {
        quote: "Asim has an exceptional ability to translate complex technical requirements into clean, maintainable code. His expertise in system design and distributed architectures is evident in everything he builds. He's also a natural mentor who lifts the entire team's engineering quality.",
        name: "Usman Khalid",
        title: "CTO",
        relationship: "Was Asim's senior",
    },
];

export const Testimonials: React.FC = () => {
    const [isMobileView, setIsMobileView] = useState(false);
    const [activeIndex, setActiveIndex] = useState(0);

    const headerRef = useGSAPReveal({ variant: 'fade-up' });
    const cardRef = useGSAPReveal({ variant: 'fade-up', delay: 0.15 });
    const linkedinRef = useGSAPReveal({ variant: 'fade-in', delay: 0.3 });

    useEffect(() => {
        const checkScreenSize = () => setIsMobileView(window.innerWidth <= 768);
        checkScreenSize();
        window.addEventListener('resize', checkScreenSize);
        return () => window.removeEventListener('resize', checkScreenSize);
    }, []);

    return (
        <section
            style={{
                padding: isMobileView ? '80px 24px 100px' : '120px 80px 140px',
                fontFamily: "'Inter', sans-serif",
                position: 'relative',
                overflow: 'hidden',
            }}
        >
            <div style={{ maxWidth: '900px', margin: '0 auto' }}>
                {/* Header */}
                <div
                    ref={headerRef}
                    style={{
                        textAlign: 'center',
                        marginBottom: isMobileView ? '48px' : '72px',
                    }}
                >
                    <div style={{ width: '40px', height: '3px', background: '#C4956A', borderRadius: '2px', margin: '0 auto 24px' }} />
                    <h2
                        style={{
                            fontSize: isMobileView ? '40px' : '56px',
                            fontWeight: 600,
                            fontFamily: "'Space Grotesk', sans-serif",
                            color: '#F5F5F5',
                            letterSpacing: '-0.02em',
                            marginBottom: '16px',
                        }}
                    >
                        What People Say
                    </h2>
                    <p style={{ fontSize: isMobileView ? '15px' : '17px', color: '#737373', fontWeight: 300 }}>
                        Feedback from colleagues and managers
                    </p>
                </div>

                {/* Testimonial Card */}
                <div ref={cardRef}>
                    <div
                        style={{
                            background: '#111111',
                            border: '1px solid rgba(245, 245, 245, 0.08)',
                            borderRadius: '24px',
                            padding: isMobileView ? '40px 28px' : '56px 64px',
                            position: 'relative',
                            overflow: 'hidden',
                            minHeight: isMobileView ? 'auto' : '260px',
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'space-between',
                        }}
                    >
                        {/* Large opening quote — copper, Space Grotesk */}
                        <div
                            style={{
                                position: 'absolute',
                                top: '16px',
                                left: isMobileView ? '20px' : '40px',
                                fontSize: '120px',
                                lineHeight: 1,
                                color: 'rgba(196, 149, 106, 0.12)',
                                fontFamily: "'Space Grotesk', sans-serif",
                                fontWeight: 400,
                                userSelect: 'none',
                            }}
                        >
                            &ldquo;
                        </div>

                        {/* Quote text */}
                        <p
                            key={activeIndex}
                            style={{
                                fontSize: isMobileView ? '17px' : '21px',
                                lineHeight: 1.8,
                                color: '#A3A3A3',
                                fontWeight: 300,
                                fontStyle: 'italic',
                                fontFamily: "'Inter', sans-serif",
                                marginBottom: '40px',
                                position: 'relative',
                                zIndex: 1,
                                animation: 'fadeInQuote 0.5s ease forwards',
                            }}
                        >
                            &ldquo;{testimonials[activeIndex].quote}&rdquo;
                        </p>

                        {/* Attribution */}
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            flexWrap: 'wrap',
                            gap: '20px',
                            position: 'relative',
                            zIndex: 1
                        }}>
                            <div>
                                <div style={{
                                    fontSize: isMobileView ? '16px' : '18px',
                                    fontWeight: 600,
                                    color: '#F5F5F5',
                                    fontFamily: "'Space Grotesk', sans-serif",
                                    marginBottom: '4px'
                                }}>
                                    {testimonials[activeIndex].name}
                                </div>
                                <div style={{ fontSize: '14px', color: '#737373', fontWeight: 400 }}>
                                    {testimonials[activeIndex].title}
                                </div>
                                <div style={{
                                    fontSize: '11px',
                                    color: '#525252',
                                    marginTop: '4px',
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.08em'
                                }}>
                                    {testimonials[activeIndex].relationship}
                                </div>
                            </div>

                            {/* Navigation dots — copper active */}
                            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                                {testimonials.map((_, i) => (
                                    <button
                                        key={i}
                                        onClick={() => setActiveIndex(i)}
                                        style={{
                                            width: i === activeIndex ? '24px' : '8px',
                                            height: '8px',
                                            borderRadius: '4px',
                                            background: i === activeIndex ? '#C4956A' : 'rgba(245, 245, 245, 0.1)',
                                            border: 'none',
                                            cursor: 'pointer',
                                            padding: 0,
                                            transition: 'all 0.3s ease',
                                        }}
                                    />
                                ))}
                            </div>
                        </div>
                    </div>
                </div>

                {/* LinkedIn social proof */}
                <div
                    ref={linkedinRef}
                    style={{
                        textAlign: 'center',
                        marginTop: '28px',
                    }}
                >
                    <a
                        href="https://linkedin.com/in/asimhafeezz"
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '8px',
                            fontSize: '13px',
                            color: '#525252',
                            textDecoration: 'none',
                            transition: 'color 0.3s ease',
                        }}
                        onMouseEnter={e => { e.currentTarget.style.color = '#C4956A'; }}
                        onMouseLeave={e => { e.currentTarget.style.color = '#525252'; }}
                    >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
                        </svg>
                        View all 6 recommendations on LinkedIn
                    </a>
                </div>
            </div>

            <style>{`
        @keyframes fadeInQuote {
          from { opacity: 0; transform: translateY(8px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
        </section>
    );
};

export default Testimonials;
